import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { CaretLeft, CaretRight, X, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsIn } from "@phosphor-icons/react";
import { StarDisplay } from "@/components/reviews/StarRating";

const Btn = ({ testId, label, onClick, children, className = "" }) => (
  <button type="button" data-testid={testId} aria-label={label} onClick={onClick}
    className={`w-11 h-11 rounded-full bg-black/50 hover:bg-black/75 border border-white/15 text-white flex items-center justify-center backdrop-blur-md transition-colors ${className}`}>
    {children}
  </button>
);

function ReviewCaption({ review, onClose }) {
  return (
    <div data-testid="viewer-caption" className="w-full md:w-80 bg-[#1C211E] text-white p-5 flex flex-col gap-2 md:h-full overflow-y-auto">
      <StarDisplay value={review.rating} size={16} dark />
      {review.title && <div className="font-display text-lg leading-snug">{review.title}</div>}
      <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line line-clamp-4 md:line-clamp-none">{review.text}</p>
      <div className="text-xs uppercase tracking-widest text-white/50">— {review.name}</div>
      {review.product_id && (
        <Link to={`/product/${review.product_id}`} onClick={onClose} data-testid="viewer-product-link"
          className="mt-2 md:mt-auto flex items-center gap-3 border border-white/15 rounded-sm p-2 hover:border-[#C84A31] transition-colors">
          {review.product_image && <img src={review.product_image} alt="" className="w-10 h-10 rounded-sm object-cover" />}
          <span className="text-sm flex-1 truncate">{review.product_title}</span>
          <CaretRight size={14} className="text-[#E8A37F]" />
        </Link>
      )}
    </div>
  );
}

function ZoomStage({ photo, onSwipe, setZoomed }) {
  const down = useRef(null);
  const [loaded, setLoaded] = useState(false);
  return (
    <TransformWrapper minScale={1} maxScale={5} doubleClick={{ disabled: true }} wheel={{ step: 0.15 }}
      onTransformed={(_, s) => setZoomed(s.scale > 1.01)}>
      {({ zoomIn, zoomOut, resetTransform, setTransform, instance }) => {
        const scaled = () => instance.transformState.scale > 1.01;
        return (
        <>
          <div data-testid="viewer-stage" className={`w-full h-full ${scaled() ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
            onPointerDownCapture={(e) => { if (e.isPrimary) down.current = { x: e.clientX, y: e.clientY, t: Date.now(), type: e.pointerType }; }}
            onPointerUpCapture={(e) => {
              const d = down.current;
              if (!d || !e.isPrimary) return;
              const dx = e.clientX - d.x, dy = e.clientY - d.y;
              d.tap = Math.abs(dx) < 6 && Math.abs(dy) < 6 && Date.now() - d.t < 300;
              if (!scaled() && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) { down.current = null; onSwipe(dx < 0 ? 1 : -1); }
            }}
            onClick={(e) => {
              const d = down.current; down.current = null;
              if (!d?.tap || d.type !== "mouse" || e.detail > 1) return;
              if (scaled()) return resetTransform(250);
              const r = e.currentTarget.getBoundingClientRect(), s = 2.5;
              setTransform(-(e.clientX - r.left) * (s - 1), -(e.clientY - r.top) * (s - 1), s, 250);
            }}>
            <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
              <img src={photo.src} alt={photo.alt || ""} data-testid="viewer-image" draggable={false} onLoad={() => setLoaded(true)}
                className={`max-w-full max-h-full object-contain select-none transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`} />
            </TransformComponent>
          </div>
          {!loaded && (
            <div data-testid="viewer-loading" className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
            </div>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            <Btn testId="viewer-zoom-in" label="Zoom in" onClick={() => zoomIn(0.6)}><MagnifyingGlassPlus size={18} /></Btn>
            <Btn testId="viewer-zoom-out" label="Zoom out" onClick={() => zoomOut(0.6)}><MagnifyingGlassMinus size={18} /></Btn>
            <Btn testId="viewer-zoom-reset" label="Reset zoom" onClick={() => resetTransform()}><ArrowsIn size={18} /></Btn>
          </div>
        </>
        );
      }}
    </TransformWrapper>
  );
}

export default function PhotoViewer({ photos, index, onIndex, onClose }) {
  const [zoomed, setZoomed] = useState(false);
  const n = photos.length;
  const go = useCallback((d) => { setZoomed(false); onIndex((index + d + n) % n); }, [index, n, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && n > 1) go(1);
      else if (e.key === "ArrowLeft" && n > 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, n, onClose]);

  const photo = photos[index];
  if (!photo) return null;
  return createPortal(
    <div data-testid="photo-viewer" role="dialog" aria-modal="true" aria-label="Photo viewer"
      className="fixed inset-0 z-[100] bg-black flex flex-col md:flex-row animate-in fade-in duration-200">
      <div className="relative flex-1 min-h-0">
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10 pointer-events-none">
          <span data-testid="viewer-counter" className="text-white/70 text-sm tracking-widest pointer-events-auto">{n > 1 ? `${index + 1} / ${n}` : ""}</span>
          <Btn testId="viewer-close" label="Close" onClick={onClose} className="pointer-events-auto"><X size={20} /></Btn>
        </div>
        <ZoomStage key={photo.src + index} photo={photo} onSwipe={(d) => n > 1 && go(d)} setZoomed={setZoomed} />
        {n > 1 && !zoomed && (
          <>
            <Btn testId="viewer-prev" label="Previous photo" onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 z-10"><CaretLeft size={22} /></Btn>
            <Btn testId="viewer-next" label="Next photo" onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 z-10"><CaretRight size={22} /></Btn>
          </>
        )}
      </div>
      {photo.review && <ReviewCaption review={photo.review} onClose={onClose} />}
    </div>,
    document.body,
  );
}
